/**
 * dashboardReleaseConfig — First-release (R1) dashboard widget visibility.
 *
 * Central allow/deny list controlling which dashboard widgets and KPI-pulse
 * tiles ship in the first release. Anything hidden here is removed entirely
 * for R1 — it does not render on the dashboard AND does not appear in the
 * settings-cog panel — so users cannot re-enable it until it is released.
 *
 * To re-enable a widget or tile in a later release, simply remove its id from
 * the relevant set below.
 */
import type { ShellPersona } from '@/contexts/DashboardWidgetPreferencesContext';

/**
 * Widget ids hidden for R1, keyed by shell persona.
 * These widgets are filtered out of the widget registry entirely.
 */
export const R1_HIDDEN_WIDGETS: Record<ShellPersona, ReadonlySet<string>> = {
  executive: new Set<string>([
    'client_concentration_risk',
    'utilisation_snapshot',
    'headcount_delivery_capacity',
    'revenue_recognition_status',
    'billing_pipeline',
    'month_end_readiness',
    'over_under_billing',
  ]),
  project_manager: new Set<string>([
    'schedule_health',
    'my_team_this_week',
    'billing_pipeline',
  ]),
};

/**
 * Individual KPI-pulse tile ids hidden for R1. Pulse widgets
 * (Portfolio Pulse, Finance Pulse, My Project Pulse) render a fixed set of
 * KPI tiles; these ids let us hide individual tiles while keeping the widget.
 */
export const R1_HIDDEN_PULSE_TILES: ReadonlySet<string> = new Set<string>([
  // Portfolio Pulse (executive)
  'sales_mtd',
  'blended_margin',
  // Finance Pulse (executive)
  'revenue_recognised_mtd',
  'deferred_revenue',
  // My Project Pulse (project manager)
  'pending_actions',
]);

/** True when a widget should be hidden from R1 for the given persona. */
export function isWidgetHiddenForRelease(
  persona: ShellPersona,
  widgetId: string,
): boolean {
  return R1_HIDDEN_WIDGETS[persona].has(widgetId);
}

/**
 * True when a widget is hidden from R1 for ANY persona. Used at the render
 * path to defensively drop hidden widgets that may linger in a user's
 * previously-saved preference order (which can include cross-persona widgets).
 */
export function isWidgetHiddenForReleaseAnyPersona(widgetId: string): boolean {
  return (
    R1_HIDDEN_WIDGETS.executive.has(widgetId) ||
    R1_HIDDEN_WIDGETS.project_manager.has(widgetId)
  );
}

/** True when a KPI-pulse tile should be hidden from R1. */
export function isPulseTileHiddenForRelease(tileId: string): boolean {
  return R1_HIDDEN_PULSE_TILES.has(tileId);
}
